import SEO, { schemas } from '../components/SEO';
import { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { submitContact } from '../services/api';
import toast from 'react-hot-toast';

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitContact(form);
      toast.success('Message sent! We will get back to you within 24 hours.');
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch {
      toast.error('Failed to send. Please call us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <SEO
        title="Contact Us — Book a Ride or Get Support"
        description="Contact MDS Transportation LLC. Call +1 (317) 982-1959, email us, or send a message online. We serve Indianapolis, Fort Wayne, Lawrence, Beech Grove, and Speedway, IN."
        path="/contact"
        schema={schemas.contact}
      />
      {/* Hero */}
      <section className="relative py-20 bg-gray-900">
        <div className="absolute inset-0">
          <img src="/images/fleet-3siennas.jpg" alt="MDS Transportation fleet" className="w-full h-full object-cover opacity-20" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-purple-300 font-semibold text-sm uppercase tracking-wider">Get In Touch</span>
          <h1 className="text-5xl font-bold text-white mt-3 mb-4">Contact Us</h1>
          <p className="text-gray-300 text-lg max-w-xl mx-auto">Have questions or need to book a ride? We're here for you 24/7.</p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Info cards */}
            <div className="space-y-6">
              {[
                { icon: Phone, label: 'Phone', value: '+1 (317) 982-1959', sub: '24/7 support available', href: 'tel:+13179821959' },
                { icon: Mail, label: 'Email', value: 'info@mdstransportation.com', sub: 'We reply within 24 hours', href: 'mailto:info@mdstransportation.com' },
                { icon: MapPin, label: 'Office', value: '5632 W Washington St', sub: 'Indianapolis, IN 46241', href: null },
                { icon: Clock, label: 'Hours', value: '24 / 7', sub: 'Always available for urgent requests', href: null },
              ].map(({ icon: Icon, label, value, sub, href }) => (
                <div key={label} className="card border border-gray-100 flex items-start gap-4">
                  <div className="w-11 h-11 bg-purple-100 rounded-xl flex items-center justify-center shrink-0">
                    <Icon size={20} className="text-purple-700" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</p>
                    {href ? (
                      <a href={href} className="font-semibold text-gray-900 hover:text-purple-700 transition-colors">{value}</a>
                    ) : (
                      <p className="font-semibold text-gray-900">{value}</p>
                    )}
                    <p className="text-sm text-gray-500 mt-0.5">{sub}</p>
                  </div>
                </div>
              ))}

              {/* Service areas */}
              <div className="card border border-gray-100">
                <h3 className="font-bold text-gray-900 mb-3 font-heading">Service Areas</h3>
                <div className="space-y-1 text-sm text-gray-600">
                  {['Fort Wayne, IN', 'Indianapolis, IN', 'Lawrence, IN', 'Beech Grove, IN', 'Speedway, IN', 'Surrounding Communities'].map(a => (
                    <div key={a} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-700" />
                      {a}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Contact form */}
            <div className="lg:col-span-2">
              <div className="card border border-gray-100 shadow-sm">
                <h2 className="text-2xl font-bold text-gray-900 mb-2 font-heading">Send Us a Message</h2>
                <p className="text-gray-500 text-sm mb-6">Fill in the form below and our team will respond within 24 hours.</p>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="label">Full Name *</label>
                      <input className="input-field" required placeholder="John Doe" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                    </div>
                    <div>
                      <label className="label">Email *</label>
                      <input className="input-field" type="email" required placeholder="john@example.com" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="label">Phone</label>
                      <input className="input-field" placeholder="+1 (555) 000-0000" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                    </div>
                    <div>
                      <label className="label">Subject</label>
                      <input className="input-field" placeholder="How can we help?" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <label className="label">Message *</label>
                    <textarea className="input-field min-h-[140px] resize-none" required placeholder="Tell us how we can assist you..." value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} />
                  </div>
                  <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2">
                    {loading ? <div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Send size={17} />}
                    {loading ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
